import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PageShell } from "@/components/layout/PageShell";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { Seal } from "@/components/brand/Seal";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { supabase } from "@/integrations/supabase/client";
import { seo } from "@/lib/seo";
import { localBusinessSchema } from "@/lib/seo-schemas";
import { ConsultCTA } from "@/components/brand/ConsultCTA";

type Founder = {
  founder_name: string;
  founder_role: string;
  founder_headshot_url: string | null;
  founder_signature_url: string | null;
  founder_short_bio: string | null;
  founder_bio_markdown: string | null;
  founder_quote: string | null;
  welcome_video_url: string | null;
  welcome_video_poster_url: string | null;
};

export const Route = createFileRoute("/about")({
  head: () =>
    seo({
      title: "About · Currency Thoughts",
      description:
        "Veteran-led, family-focused. Founded 2021 to build calm, generational infrastructure for Strategists and the families they serve.",
      canonical: "/about",
      schema: localBusinessSchema(),
    }),
  component: AboutPage,
});

function AboutPage() {
  const [f, setF] = useState<Founder | null>(null);
  useEffect(() => {
    const load = () =>
      supabase.from("founder_content").select("*").limit(1).maybeSingle()
        .then(({ data }) => { if (data) setF(data as Founder); });
    void load();
    const channel = supabase
      .channel("founder_content-about")
      .on("postgres_changes", { event: "*", schema: "public", table: "founder_content" }, () => { void load(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, []);

  return (
    <PageShell
      eyebrow="Our Story"
      title="About"
      intro="A veteran-led independent agency building Family Legacy Infrastructure — calm, generational, and built to outlast a single transaction."
    >
      {/* SECTION 1 — Lead Paragraph */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[720px] px-6">
          <p className="font-sans text-[17px] leading-[1.65] text-charcoal-900">
            <span className="font-display italic text-[22px] leading-[1.4] text-navy-900 block mb-4">
              Currency Thoughts is a veteran-led independent insurance agency founded on a simple conviction: families deserve more than products.
            </span>
            They deserve education, tools, and a coaching relationship that holds up over generations. We work with families the way we wish our own families had been worked with — calmly, honestly, and with a long-term plan that protects what they&rsquo;ve built today and positions their children&rsquo;s children for a different starting line than the one they were given.
          </p>
        </div>
      </section>

      {/* SECTION 2 — Our Story */}
      <section className="bg-cream-50 py-20 border-t border-bronze-600/40">
        <div className="mx-auto max-w-[720px] px-6">
          <Eyebrow>Our Story</Eyebrow>
          <h2 className="mt-6 font-display font-semibold text-navy-900 text-[36px] md:text-[44px] leading-[1.15]">
            We started Currency Thoughts to answer a question we couldn&rsquo;t shake.
          </h2>
          <div className="mt-10 font-sans text-[17px] leading-[1.65] text-charcoal-900 space-y-6">
            <p>
              <span className="float-left font-display text-bronze-600 text-[64px] leading-[0.85] mr-3 mt-1">W</span>
              e founded Currency Thoughts in 2021 with a question we couldn&rsquo;t shake: why does the most consequential financial decision a family makes — protecting income and transferring wealth across generations — get treated like a transaction? Why does insurance get sold by people who don&rsquo;t stay long enough to coach the family through the decisions that come after the policy is placed?
            </p>
            <p>
              We started this agency to be the answer. We are licensed independent agents, and we chose an independent structure deliberately: it lets us stay with a family through the decisions that come after the paperwork. The work itself happens at the kitchen table, not the boardroom.
            </p>
          </div>
        </div>
      </section>

      {/* PULL QUOTE 1 */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-[820px] px-6 text-center">
          <span className="font-display text-bronze-600 text-[72px] leading-none block -mb-6" aria-hidden>
            &ldquo;
          </span>
          <blockquote className="font-display italic text-navy-900 text-[28px] leading-[1.3]">
            Why does the most consequential financial decision a family makes get treated like a transaction?
          </blockquote>
        </div>
      </section>

      {/* SECTION 3 — What We Build */}
      <section className="bg-white py-20 border-t border-bronze-600/40">
        <div className="mx-auto max-w-[720px] px-6">
          <Eyebrow>What We Build</Eyebrow>
          <h2 className="mt-6 font-display font-semibold text-navy-900 text-[36px] md:text-[44px] leading-[1.15]">
            Family Legacy Infrastructure.
          </h2>
          <div className="mt-10 font-sans text-[17px] leading-[1.65] text-charcoal-900 space-y-6">
            <p>
              <span className="float-left font-display text-bronze-600 text-[64px] leading-[0.85] mr-3 mt-1">W</span>
              hat we build for every family we serve is called Family Legacy Infrastructure. It is the calm, organized, multi-generational way of thinking that protects what you have today and prepares the next generation to inherit not just assets, but understanding.
            </p>
            <p>
              Every family receives access to our platform: the Family Snapshot diagnostic, the Legacy Score readiness assessment, our Academy of financial literacy, and the Currency Thoughts Store — our library of teaching and workbooks for families who want to steward what they build with intention.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 — The Role */}
      <section className="bg-cream-50 py-20">
        <div className="mx-auto max-w-[720px] px-6 text-center">
          <div className="flex justify-center">
            <Seal title="CT" topLabel="The Role" bottomLabel="Coach · Not Salesperson" size="sm" />
          </div>
          <div className="mt-8">
            <Eyebrow align="center">The Role</Eyebrow>
          </div>
          <h2 className="mt-6 font-display font-semibold text-navy-900 text-[36px] md:text-[44px] leading-[1.15]">
            Our strategists don&rsquo;t sell insurance.
          </h2>
          <div className="mt-10 font-sans text-[17px] leading-[1.65] text-charcoal-900 space-y-6">
            <p>Our strategists serve as Economic Empowerment Coaches. The distinction matters.</p>
            <p>
              A salesperson closes a deal and moves on. A coach walks with a family through every decision that determines whether the plan actually works — beneficiary audits, anniversary reviews, policy adjustments as life changes, and the difficult conversations most agents avoid. The coach is what makes the protection actually protect.
            </p>
          </div>
        </div>
      </section>

      {/* PULL QUOTE 2 */}
      <section className="bg-navy-900 py-20">
        <div className="mx-auto max-w-[820px] px-6 text-center">
          <span className="font-display text-bronze-400 text-[72px] leading-none block -mb-6" aria-hidden>
            &ldquo;
          </span>
          <blockquote className="font-display italic text-cream-50 text-[32px] leading-[1.3]">
            The coach is what makes the protection actually protect.
          </blockquote>
        </div>
      </section>

      {/* SECTION 5 — Two Paths */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[720px] px-6">
          <Eyebrow>If You&rsquo;ve Found Us</Eyebrow>
          <h2 className="mt-6 font-display font-semibold text-navy-900 text-[36px] md:text-[44px] leading-[1.15]">
            Two paths to the same standard.
          </h2>
          <div className="mt-10 font-sans text-[17px] leading-[1.65] text-charcoal-900 space-y-6">
            <p>
              <span className="float-left font-display text-bronze-600 text-[64px] leading-[0.85] mr-3 mt-1">I</span>
              f you&rsquo;ve found us because your family needs real coverage and real coaching, you&rsquo;re in the right room.
            </p>
            <p>
              If you&rsquo;ve found us because you&rsquo;re a licensed strategist — or could be one — who has been looking for the kind of mission, platform, and team that takes the long view of this work, you&rsquo;re also in the right room.
            </p>
            <p>
              We don&rsquo;t promise easy answers or fast money. We promise honest work, calm thinking, and a relationship that holds up over generations. That&rsquo;s the Currency Thoughts difference. It&rsquo;s also our standard.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-900 py-20">
        <div className="mx-auto max-w-[820px] px-6 text-center">
          <h2 className="font-display font-semibold text-cream-50 text-[36px] leading-[1.2]">
            Where would you like to begin?
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6">
            <ConsultCTA />
            <SecondaryButton asChild size="lg">
              <Link to="/careers">Explore Becoming a Strategist →</Link>
            </SecondaryButton>
          </div>
        </div>
      </section>

      {f?.founder_quote && (
        <section className="bg-navy-900 text-cream-50 py-20">
          <div className="container mx-auto px-6 md:px-10 max-w-4xl text-center">
            <Eyebrow color="cream" align="center">From the Founder</Eyebrow>
            <blockquote className="mt-8 font-display italic text-cream-50 text-[28px] md:text-[36px] leading-[1.25]">
              &ldquo;{f.founder_quote}&rdquo;
            </blockquote>
            <p className="mt-6 text-bronze-400 font-sans text-[11px] uppercase tracking-[0.22em]">
              — {f.founder_name}
            </p>
          </div>
        </section>
      )}

      {f?.welcome_video_url && (
        <section className="bg-cream-50 py-20">
          <div className="container mx-auto px-6 md:px-10 max-w-4xl">
            <Eyebrow align="center">Welcome</Eyebrow>
            <h2 className="mt-4 text-center font-display font-semibold text-navy-800 text-[32px] md:text-[40px]">
              A word from <em className="text-bronze-600 italic font-normal">{f.founder_name}</em>
            </h2>
            <div className="mt-10 ring-2 ring-bronze-600/60 bg-navy-900 rounded-sm overflow-hidden shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)]">
              <video
                className="w-full aspect-video"
                controls
                preload="metadata"
                poster={f.welcome_video_poster_url ?? undefined}
                src={f.welcome_video_url}
              />
            </div>
          </div>
        </section>
      )}

      {(f?.founder_bio_markdown || f?.founder_headshot_url) && (
        <section className="bg-cream-50 py-20 border-t border-bronze-600/20">
          <div className="container mx-auto px-6 md:px-10 max-w-5xl">
            <Eyebrow align="center">Meet the Founder</Eyebrow>
            <div className="mt-10 grid md:grid-cols-[280px_1fr] gap-10 items-start">
              {f.founder_headshot_url && (
                <img
                  src={f.founder_headshot_url}
                  alt={f.founder_name}
                  className="w-full aspect-square object-cover ring-2 ring-bronze-600/50"
                />
              )}
              <div>
                <h3 className="font-display text-navy-800 text-[28px]">{f.founder_name}</h3>
                <p className="mt-1 text-bronze-600 font-sans text-[11px] uppercase tracking-[0.22em]">{f.founder_role}</p>
                {f.founder_bio_markdown && (
                  <div
                    className="mt-6 prose prose-sm max-w-none font-sans text-charcoal-900 text-[16px] leading-[1.7] [&_p]:mb-4"
                    dangerouslySetInnerHTML={{ __html: f.founder_bio_markdown }}
                  />
                )}
                {f.founder_signature_url && (
                  <div className="mt-8 pt-6 border-t border-bronze-600/30">
                    <img src={f.founder_signature_url} alt={`${f.founder_name} signature`} className="h-16 object-contain" />
                    <p className="mt-2 font-sans text-[11px] uppercase tracking-[0.22em] text-charcoal-500">{f.founder_name}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}